import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'

import {FormattedMessage, useIntl} from 'react-intl'
import {Box, Heading, Stack, Button} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useForm} from 'react-hook-form'
import FieldsetForm from '../../components/shared/ui/FieldsetForm'

import colors from '../../theme/foundations/colors'
import AccountPrivacyFields from '../../components/forms/account-privacy-fields'
import {useUpdateProfileFields} from './../../hooks/use-update-profile-fields'
import {useGDPRPolicy} from './../../hooks/use-gdpr-policy'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useToast} from '../../hooks/use-toast'
import {PRIVACY_POLICY_URL} from '../../constants'
import {useSplioService} from './../../hooks/use-splio-service'
import {getLocalStorageJSONItem} from '../../utils/utils'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

/**
 * AccountPrivacy component renders a page for managing account privacy settings.
 *
 * This component uses `react-hook-form` for form management and `react-intl` for internationalization.
 * It includes functionality to send test alerts when in test mode, and focuses the heading for accessibility.
 *
 * @component
 * @returns {JSX.Element} The rendered component.
 */
const AccountPrivacy = () => {
    const {data: customer} = useCurrentCustomer()
    const {sendPrivacyPageEmail} = useGDPRPolicy()
    const {updatePrivacyFields} = useUpdateProfileFields()
    const form = useForm()
    const showToast = useToast()
    const {formatMessage} = useIntl()
    const {forwardAccountToSplio, unsubscribeAccount} = useSplioService()
    const {usePageLoadAndCookies} = useGTM()

    const [initialConsents, setInitialConsents] = useState({})

    useEffect(() => {
        setInitialConsents({
            marketingConsent: customer?.c_marketingConsent,
            shareConsent: customer?.c_shareConsent
        })
    }, [customer])

    /**
     * Submits the account privacy form with the provided data.
     *
     * This asynchronous function handles the submission of privacy preferences
     * for a user's account. It retrieves an access token, updates privacy fields
     * based on the user's consent for analytical and promotional communications,
     * and optionally sends a privacy page email if the user has not consented
     * to share analytical data.
     *
     * @async
     * @function submitAccountPrivacyForm
     * @param {Object} data - The data containing user's privacy preferences.
     * @param {boolean} data.analytical - User's consent for analytical data sharing.
     * @param {boolean} data.promo - User's consent for promotional communications.
     * @returns {Promise<void>} A promise that resolves when the form submission is complete.
     */
    const submitAccountPrivacyForm = async (data) => {
        const shareConsent = Boolean(data.analytical)
        const marketingConsent = Boolean(data.promo)

        const isSameShareConsent = shareConsent === initialConsents.shareConsent
        const isSameMarketingConsent = marketingConsent === initialConsents.marketingConsent

        await updatePrivacyFields(
            marketingConsent,
            shareConsent,
            !isSameMarketingConsent,
            !isSameShareConsent
        )

        if (marketingConsent) {
            await forwardAccountToSplio({
                email: customer?.email,
                firstName: customer?.firstName,
                lastName: customer?.lastName,
                phoneHome: customer?.phoneHome,
                birthday: customer?.birthday,
                marketingConsent: marketingConsent,
                newsletterSection: 'body',
                requestType: 'account-privacy'
            })
        } else {
            await unsubscribeAccount({
                id: customer?.email,
                requestType: 'account-privacy'
            })
        }

        if (!shareConsent) {
            await sendPrivacyPageEmail({
                emailID: customer?.email,
                emailType: 'privacyPage'
            })
        }

        showToast({
            title: formatMessage({
                defaultMessage: 'Privacy settings updated',
                id: 'account_privacy.update_success'
            }),
            status: 'success'
        })
    }

    usePageLoadAndCookies('account')

    return (
        <Stack data-testid="account-privacy-page" spacing={24}>
            <Box>
                <Heading
                    as="h1"
                    display="inline-block"
                    size="3xl"
                    textTransform="uppercase"
                    tabIndex="0"
                >
                    <FormattedMessage
                        defaultMessage="Privacy Information"
                        id="account_privacy.title"
                    />
                </Heading>
            </Box>

            <Stack spacing={24}>
                <PrivacyCard form={form} submitForm={submitAccountPrivacyForm} />
            </Stack>
        </Stack>
    )
}

AccountPrivacy.getTemplateName = () => 'account-privacy'

export default AccountPrivacy

/**
 * PrivacyCard component renders a form for managing account privacy settings.
 *
 * This component includes fields for privacy preferences and a submit button.
 * It uses the `react-hook-form` library for form management and `react-intl` for internationalization.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.form - The form object containing form state and handlers.
 * @param {Function} props.submitForm - The function to handle form submission.
 * @returns {JSX.Element} The rendered component.
 */
const PrivacyCard = ({form, submitForm}) => {
    const {formatMessage} = useIntl()

    return (
        <Box
            bg={'white'}
            py={'24px'}
            px={{base: '16px', lg: '50px'}}
            border={`1px solid ${colors.borderGray}`}
        >
            <FieldsetForm
                legendText={formatMessage({
                    defaultMessage: 'Account privacy form',
                    id: 'set_new_password_form.form.legend'
                })}
                onSubmit={form.handleSubmit(submitForm)}
                error={form.formState.errors?.global?.message}
                isMandatoryNote={false}
            >
                <AccountPrivacyFields form={form} />
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    gap={'32px'}
                    ml={{base: 0, lg: '20px'}}
                >
                    <Button
                        type="submit"
                        onClick={() => form.clearErrors('global')}
                        isLoading={form.formState.isSubmitting}
                        variant="primary"
                        width={{base: '50%', lg: '112px'}}
                        aria-label={formatMessage({
                            defaultMessage: 'Submit account privacy form',
                            id: 'account_privacy.submit_label'
                        })}
                    >
                        <FormattedMessage defaultMessage="Update" id="account_privacy.submit" />
                    </Button>
                    <Link
                        to={PRIVACY_POLICY_URL}
                        width={{base: '50%', lg: 'unset'}}
                        textAlign={{base: 'center', lg: 'unset'}}
                    >
                        <FormattedMessage
                            defaultMessage="View privacy policy"
                            id="account_privacy.view_privacy"
                        />
                    </Link>
                </Stack>
            </FieldsetForm>
        </Box>
    )
}
PrivacyCard.propTypes = {
    form: PropTypes.object.isRequired,
    submitForm: PropTypes.func
}
